/**
 * Ledger Store
 *
 * Provides global ledger selection and management.
 * Persists the selected ledger ID to localStorage.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'

import { useOrganization } from './organization-store'
import {
  type LocalLedger,
  type LedgerType,
  getLocalLedgers,
  getLocalLedger,
  createLocalLedger,
  getDefaultLedgerId,
  initializeLocalDb,
} from '../services/local-db'

// ============================================================================
// Types
// ============================================================================

export interface LedgerContextValue {
  // Current state
  ledgers: LocalLedger[]
  currentLedger: LocalLedger | null
  currentLedgerId: string | null
  isLoading: boolean
  isLoaded: boolean

  // Actions
  selectLedger: (ledgerId: string) => Promise<void>
  refreshLedgers: () => Promise<void>
  createLedger: (data: CreateLedgerInput) => Promise<LocalLedger>
}

export interface CreateLedgerInput {
  code: string
  name: string
  description?: string
  ledgerType: LedgerType
  currency: string
  parentLedgerId?: string
}

// ============================================================================
// Constants
// ============================================================================

const STORAGE_KEY = 'elaccounts_selected_ledger'

// ============================================================================
// Context
// ============================================================================

const LedgerContext = createContext<LedgerContextValue | null>(null)

// ============================================================================
// Provider
// ============================================================================

// Helper function to select the first available ledger
function selectFirstAvailableLedger(
  loadedLedgers: LocalLedger[],
  setCurrentLedgerId: (id: string) => void,
  setCurrentLedger: (ledger: LocalLedger) => void
): void {
  if (loadedLedgers.length > 0) {
    const first = loadedLedgers[0]
    if (first) {
      setCurrentLedgerId(first.id)
      setCurrentLedger(first)
    }
  }
}

interface ValidateAndSetLedgerParams {
  organizationId: string
  selectedId: string
  loadedLedgers: LocalLedger[]
  setCurrentLedgerId: (id: string) => void
  setCurrentLedger: (ledger: LocalLedger | null) => void
}

// Helper function to validate and set a selected ledger
async function validateAndSetLedger(params: ValidateAndSetLedgerParams): Promise<void> {
  const { organizationId, selectedId, loadedLedgers, setCurrentLedgerId, setCurrentLedger } = params
  const ledger = await getLocalLedger(organizationId, selectedId)
  if (ledger) {
    setCurrentLedgerId(selectedId)
    setCurrentLedger(ledger)
  } else if (loadedLedgers.length > 0) {
    // Fallback to first available ledger
    selectFirstAvailableLedger(loadedLedgers, setCurrentLedgerId, setCurrentLedger)
  }
}

interface LedgerInitializationParams {
  organizationId: string
  setLedgers: (ledgers: LocalLedger[]) => void
  setCurrentLedgerId: (id: string) => void
  setCurrentLedger: (ledger: LocalLedger | null) => void
}

async function initializeLedgersForOrganization(params: LedgerInitializationParams): Promise<void> {
  const { organizationId, setLedgers, setCurrentLedgerId, setCurrentLedger } = params
  await initializeLocalDb(organizationId)

  const loadedLedgers = await getLocalLedgers(organizationId)
  setLedgers(loadedLedgers)

  const storedLedgerId = localStorage.getItem(STORAGE_KEY)
  const selectedId = storedLedgerId ?? getDefaultLedgerId()

  if (selectedId) {
    await validateAndSetLedger({ organizationId, selectedId, loadedLedgers, setCurrentLedgerId, setCurrentLedger })
  } else if (loadedLedgers.length > 0) {
    selectFirstAvailableLedger(loadedLedgers, setCurrentLedgerId, setCurrentLedger)
  } else {
    // No ledgers available
  }
}

interface LedgerStateSetters {
  setLedgers: (ledgers: LocalLedger[]) => void
  setCurrentLedgerId: (id: string | null) => void
  setCurrentLedger: (ledger: LocalLedger | null) => void
  setIsLoading: (loading: boolean) => void
  setIsLoaded: (loaded: boolean) => void
}

function useLedgerInitialization(organizationId: string, isOrgInitializing: boolean, setters: LedgerStateSetters): void {
  const { setLedgers, setCurrentLedgerId, setCurrentLedger, setIsLoading, setIsLoaded } = setters
  useEffect(() => {
    async function initialize(): Promise<void> {
      if (!organizationId) {
        if (isOrgInitializing) {
          // Org store is still resolving — do NOT mark as loaded yet.
          // SetupGuard will wait on isInitializing anyway, but marking
          // isLoaded=true here with 0 ledgers creates a race condition
          // when isInitializing flips to false in the same render cycle.
          return
        }
        // No organization yet - mark as loaded but with empty ledgers
        setLedgers([])
        setCurrentLedgerId(null)
        setCurrentLedger(null)
        setIsLoading(false)
        setIsLoaded(true)
        return
      }
      setIsLoading(true)
      setIsLoaded(false)
      try {
        await initializeLedgersForOrganization({ organizationId, setLedgers, setCurrentLedgerId, setCurrentLedger })
        // initialization complete
      } catch {
        // silently ignore initialization failure
      } finally {
        setIsLoading(false)
        setIsLoaded(true)
      }
    }
    void initialize()
  }, [organizationId, isOrgInitializing, setLedgers, setCurrentLedgerId, setCurrentLedger, setIsLoading, setIsLoaded])
}

function useLedgerPersistence(currentLedgerId: string | null, isLoaded: boolean): void {
  useEffect(() => {
    if (isLoaded && currentLedgerId) {
      localStorage.setItem(STORAGE_KEY, currentLedgerId)
    }
  }, [currentLedgerId, isLoaded])
}

async function selectLedgerById(
  organizationId: string,
  ledgerId: string,
  setCurrentLedgerId: (id: string) => void,
  setCurrentLedger: (ledger: LocalLedger | null) => void
): Promise<void> {
  const ledger = await getLocalLedger(organizationId, ledgerId)
  if (ledger) {
    setCurrentLedgerId(ledgerId)
    setCurrentLedger(ledger)
  }
}

function useRefreshLedgers(
  organizationId: string,
  currentLedgerId: string | null,
  setters: Pick<LedgerStateSetters, 'setLedgers' | 'setIsLoading' | 'setCurrentLedgerId' | 'setCurrentLedger'>
): () => Promise<void> {
  const { setLedgers, setIsLoading, setCurrentLedgerId, setCurrentLedger } = setters
  return useCallback(async () => {
    if (!organizationId) {
      return
    }
    setIsLoading(true)
    try {
      const loadedLedgers = await getLocalLedgers(organizationId)
      setLedgers(loadedLedgers)

      // If no current ledger is selected and we have ledgers, select the first one
      if (!currentLedgerId && loadedLedgers.length > 0) {
        const defaultId = getDefaultLedgerId()
        if (defaultId) {
          const ledger = await getLocalLedger(organizationId, defaultId)
          if (ledger) {
            setCurrentLedgerId(defaultId)
            setCurrentLedger(ledger)
          } else {
            // Fallback to first ledger
            selectFirstAvailableLedger(loadedLedgers, setCurrentLedgerId, setCurrentLedger)
          }
        } else {
          selectFirstAvailableLedger(loadedLedgers, setCurrentLedgerId, setCurrentLedger)
        }
      }
    } catch {
      // silently ignore refresh failure
    } finally {
      setIsLoading(false)
    }
  }, [organizationId, currentLedgerId, setIsLoading, setLedgers, setCurrentLedgerId, setCurrentLedger])
}

function useSelectLedger(organizationId: string, setters: Pick<LedgerStateSetters, 'setCurrentLedgerId' | 'setCurrentLedger' | 'setIsLoading'>): (ledgerId: string) => Promise<void> {
  const { setCurrentLedgerId, setCurrentLedger, setIsLoading } = setters
  return useCallback(async (ledgerId: string) => {
    if (!organizationId) {
      return
    }
    setIsLoading(true)
    try {
      await selectLedgerById(organizationId, ledgerId, setCurrentLedgerId, setCurrentLedger)
    } catch {
      // silently ignore selection failure
    } finally {
      setIsLoading(false)
    }
  }, [organizationId, setIsLoading, setCurrentLedgerId, setCurrentLedger])
}

function useCreateLedger(organizationId: string, refreshLedgers: () => Promise<void>): (data: CreateLedgerInput) => Promise<LocalLedger> {
  return useCallback(async (data: CreateLedgerInput): Promise<LocalLedger> => {
    if (!organizationId) {
      throw new Error('Cannot create ledger: No organization ID')
    }
    const newLedger = await createLocalLedger(organizationId, {
      ...data,
      description: data.description ?? '',
      isActive: true,
    })
    await refreshLedgers()
    return newLedger
  }, [organizationId, refreshLedgers])
}

export function LedgerProvider({ children }: { children: ReactNode }): JSX.Element {
  const [ledgers, setLedgers] = useState<LocalLedger[]>([])
  const [currentLedger, setCurrentLedger] = useState<LocalLedger | null>(null)
  const [currentLedgerId, setCurrentLedgerId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  const { organizationId, isInitializing: isOrgInitializing } = useOrganization()

  // Synchronously reset loaded state when organizationId changes.
  // This prevents the race where SetupGuard sees stale isLoaded=true
  // from the previous org before the useEffect can fire.
  const prevOrgIdRef = useRef(organizationId)
  if (prevOrgIdRef.current !== organizationId) {
    prevOrgIdRef.current = organizationId
    if (isLoaded || !isLoading) {
      setIsLoaded(false)
      setIsLoading(true)
    }
  }

  const setters: LedgerStateSetters = { setLedgers, setCurrentLedgerId, setCurrentLedger, setIsLoading, setIsLoaded }

  useLedgerInitialization(organizationId, isOrgInitializing, setters)
  useLedgerPersistence(currentLedgerId, isLoaded)

  const refreshLedgers = useRefreshLedgers(organizationId, currentLedgerId, { setLedgers, setIsLoading, setCurrentLedgerId, setCurrentLedger })
  const selectLedger = useSelectLedger(organizationId, { setCurrentLedgerId, setCurrentLedger, setIsLoading })
  const createLedger = useCreateLedger(organizationId, refreshLedgers)

  return (
    <LedgerContext.Provider
      value={{
        ledgers,
        currentLedger,
        currentLedgerId,
        isLoading,
        isLoaded,
        selectLedger,
        refreshLedgers,
        createLedger,
      }}
    >
      {children}
    </LedgerContext.Provider>
  )
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Access the full ledger context
 */
export function useLedgerContext(): LedgerContextValue {
  const context = useContext(LedgerContext)
  if (!context) {
    throw new Error('useLedgerContext must be used within a LedgerProvider')
  }
  return context
}

/**
 * Get the current ledger ID (convenience hook)
 */
export function useCurrentLedgerId(): string | null {
  const { currentLedgerId } = useLedgerContext()
  return currentLedgerId
}

/**
 * Like useCurrentLedgerId, but returns null when no LedgerProvider is mounted
 * instead of throwing. Useful for components rendered in test environments or
 * outside the provider that only need the ledger id when actually used.
 */
export function useOptionalCurrentLedgerId(): string | null {
  const ctx = useContext(LedgerContext)
  return ctx?.currentLedgerId ?? null
}

/**
 * Get the current ledger (convenience hook)
 */
export function useCurrentLedger(): LocalLedger | null {
  const { currentLedger } = useLedgerContext()
  return currentLedger
}

/**
 * Get all available ledgers
 */
export function useLedgers(): LocalLedger[] {
  const { ledgers } = useLedgerContext()
  return ledgers
}

// Re-export types from local-db for convenience
export type { LocalLedger, LedgerType }
